// Нативное распознавание речи Android (Capacitor-плагин @capacitor-community/
// speech-recognition). Используется в собранном APK, где браузерный
// webkitSpeechRecognition недоступен. Интерфейс намеренно совпадает с
// SpeechRecognizer (browser), чтобы вызывающий код (useConversation) не менялся:
// один заход = одна реплика, рестарт прослушивания делает вызывающий.
//
// Распознавание идёт системным движком Android (Google) — аудио обрабатывается
// устройством, наружу уходит уже готовый текст, как и в браузерной версии.

import { SpeechRecognition } from '@capacitor-community/speech-recognition'
import type { PluginListenerHandle } from '@capacitor/core'
import type { SpeechRecognizerOptions } from './SpeechRecognizer'

export class NativeSpeechRecognizer {
  readonly supported = true
  private opts: SpeechRecognizerOptions
  private manualStop = false
  private latest = ''
  private listening = false
  private partialSub: PluginListenerHandle | null = null
  private stateSub: PluginListenerHandle | null = null

  constructor(opts: SpeechRecognizerOptions) {
    this.opts = opts
  }

  private async ensureListeners(): Promise<void> {
    if (!this.partialSub) {
      this.partialSub = await SpeechRecognition.addListener('partialResults', (data) => {
        const text = (data.matches?.[0] ?? '').trim()
        if (text) {
          this.latest = text
          if (!this.manualStop) this.opts.onInterim(text)
        }
      })
    }
    if (!this.stateSub) {
      this.stateSub = await SpeechRecognition.addListener('listeningState', (data) => {
        if (data.status === 'stopped') this.finish()
      })
    }
  }

  private finish(): void {
    if (!this.listening) return
    this.listening = false
    const manual = this.manualStop
    const text = this.latest
    this.latest = ''
    if (!manual && text) this.opts.onFinal(text)
    this.opts.onEnd?.(manual)
  }

  async start(): Promise<void> {
    this.manualStop = false
    this.latest = ''
    try {
      const perm = await SpeechRecognition.checkPermissions()
      if (perm.speechRecognition !== 'granted') {
        const req = await SpeechRecognition.requestPermissions()
        if (req.speechRecognition !== 'granted') {
          this.opts.onError?.('not-allowed')
          return
        }
      }
      await this.ensureListeners()
      this.listening = true
      // partialResults:true → start() резолвится сразу, текст приходит событиями
      // partialResults; popup:false — слушаем без системного диалога.
      await SpeechRecognition.start({
        language: this.opts.lang,
        partialResults: true,
        popup: false,
        maxResults: 1,
      })
    } catch (e) {
      this.listening = false
      this.opts.onError?.(String((e as Error)?.message ?? e))
    }
  }

  async stop(): Promise<void> {
    this.manualStop = true
    try {
      await SpeechRecognition.stop()
    } catch {
      /* noop */
    }
  }

  async abort(): Promise<void> {
    await this.stop()
  }
}
